import {put, call} from 'redux-saga/effects';
import CourtDeadlineRule from "../../entities/courtDeadlineRule";
import * as Actions from "../actions/courtDeadlineRuleActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CourtDeadlineRuleAction = {
    courtDeadlineRuleId: string;
    courtDeadlineRule: CourtDeadlineRule;
    courtDeadlineRules: CourtDeadlineRule[];
};

export function* fetchOneCourtDeadlineRule(action: CourtDeadlineRuleAction) {
	try {
		const courtDeadlineRule: CourtDeadlineRule = yield call(api.findOne, 'courtDeadlineRule', action.courtDeadlineRuleId);

		// Now we can update the user interface
		yield put({type: Actions.COURTDEADLINERULE_FETCH_ONE_SUCCESS, courtDeadlineRuleId: action.courtDeadlineRuleId, courtDeadlineRule});
	}
	catch(err) {
		yield put({type: Actions.COURTDEADLINERULE_FETCH_ONE_FAILURE, courtDeadlineRuleId: action.courtDeadlineRuleId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CourtDeadlineRuleAction} action
 */
export function* saveCourtDeadlineRule(action: CourtDeadlineRuleAction) {
    // console.debug('⮂ COURTDEADLINERULE_SAVE saga', action.courtDeadlineRule?.id);
    
	if (action.courtDeadlineRule === undefined || action.courtDeadlineRule === null) {
		console.error('saveCourtDeadlineRule received a null');
		throw new Error('saveCourtDeadlineRule received a null');
	}

	if (!(action.courtDeadlineRule instanceof CourtDeadlineRule)) {
		action.courtDeadlineRule = new CourtDeadlineRule(action.courtDeadlineRule);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'courtDeadlineRule', action.courtDeadlineRule);
		if (addResult.isErr()) {
			console.error(`⮂ saveCourtDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COURTDEADLINERULE_SAVE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COURTDEADLINERULE_SAVE, courtDeadlineRule: action.courtDeadlineRule});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'courtDeadlineRule', action.courtDeadlineRule.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.COURTDEADLINERULE_SAVE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {CourtDeadlineRuleAction} action
 * @param {CourtDeadlineRule[]} action.courtDeadlineRules
 */
export function* saveManyCourtDeadlineRules(action: CourtDeadlineRuleAction) {
    // console.debug('⮂ COURTDEADLINERULE_SAVE_MANY saga', action.courtDeadlineRules.map(courtDeadlineRule => courtDeadlineRule.id).join(','));
    
    if (!Array.isArray(action.courtDeadlineRules)) {
        console.error('saveManyCourtDeadlineRules expected an array, received', action.courtDeadlineRules);
        throw new Error('saveManyCourtDeadlineRules expected an array');
    }

	try {
		const toSave: CourtDeadlineRule[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.courtDeadlineRules.length; i++) {
			const courtDeadlineRule = action.courtDeadlineRules[i];
			courtDeadlineRule.persisted = false;
			toSave.push(courtDeadlineRule);
		}

		const addResult = yield call(idb.add, 'courtDeadlineRule', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyCourtDeadlineRules: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COURTDEADLINERULE_SAVE_MANY_FAILURE, courtDeadlineRules: action.courtDeadlineRules, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COURTDEADLINERULE_SAVE_MANY, courtDeadlineRules: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.COURTDEADLINERULE_SAVE_MANY_FAILURE, courtDeadlineRules: action.courtDeadlineRules, status: 500, err});

		console.error(`Error saving ${action.courtDeadlineRules.length} courtDeadlineRules to database`, err);
	}
}

/**
 * @param {CourtDeadlineRuleAction} action
 */
export function* removeCourtDeadlineRule(action: CourtDeadlineRuleAction) {
    const userId = getCurrentUserId();
	if (!userId || action.courtDeadlineRule === undefined || action.courtDeadlineRule === null || !(action.courtDeadlineRule instanceof CourtDeadlineRule)) {
			console.error('removeCourtDeadlineRule expected type CourtDeadlineRule, received', action.courtDeadlineRule);
			throw new Error('removeCourtDeadlineRule expected type CourtDeadlineRule');
		}

	try {
		// Mark as deleted
		action.courtDeadlineRule.status = 'D';
		action.courtDeadlineRule.addedOn = newHRTimestamp();
		action.courtDeadlineRule.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'courtDeadlineRule', action.courtDeadlineRule);
		if (addResult.isErr()) {
			console.error(`⮂ removeCourtDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COURTDEADLINERULE_REMOVE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COURTDEADLINERULE_REMOVE, courtDeadlineRule: action.courtDeadlineRule});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.COURTDEADLINERULE_REMOVE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this CourtDeadlineRule.
	*
	* @param {CourtDeadlineRuleAction} action
	*/
export function* restoreCourtDeadlineRule(action: CourtDeadlineRuleAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCourtDeadlineRule expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.courtDeadlineRule === undefined || action.courtDeadlineRule === null) {
        console.error('restoreCourtDeadlineRule expected a CourtDeadlineRule, received null');
        throw new Error('restoreCourtDeadlineRule expected a CourtDeadlineRule, received null');
    }
    else if (!(action.courtDeadlineRule instanceof CourtDeadlineRule)) action.courtDeadlineRule = new CourtDeadlineRule(action.courtDeadlineRule); 
    
	try {
		// Mark as restored
		action.courtDeadlineRule.status = 'U';
		action.courtDeadlineRule.addedOn = newHRTimestamp();
		action.courtDeadlineRule.addedBy = userId;
		action.courtDeadlineRule.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'courtDeadlineRule', action.courtDeadlineRule);
		if (addResult.isErr()) {
			console.error(`⮂ restoreCourtDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COURTDEADLINERULE_RESTORE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COURTDEADLINERULE_RESTORE, courtDeadlineRule: action.courtDeadlineRule});
	}
	catch(err) {
		yield put({type: Actions.COURTDEADLINERULE_RESTORE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, err});

		console.error('Error restoring from database', err);
	}
}